<template>
	<div class="nav vue-version-switch" v-if="visible">
		<button
			class="vue-version-switch__display-switcher-btn"
			v-show="!displaySwitcher"
			@click="toggleSwitcher"
			id="vueVersionSwitch"
			ref="vueVersionSwitch"
		>
			<i class="icon icon-swap" />
			<Tooltip v-if="showTooltip">
				Change Screen Layout
			</Tooltip>
		</button>
		<Popover
			trackedRefName="vueVersionSwitch"
			:visible="popoverVisible"
			@close="onPopoverClose"
			placement="right"
			ref="popover"
			positionStrategy="fixed"
			:container="popoverOptions.container"
			:zIndex="popoverOptions.zIndex"
			:offset="[0, 20]"
		>
			<template v-slot:popoverTitle>
				<div class="vue-version-switch__popover-title">
					<i class="icon icon-warning" />
					<span class="vue-version-switch__popover-title-text">
						We have a new look!
					</span>
				</div>
			</template>
			<template v-slot:popoverBody>
				Click here to enable the updated experience for this screen
			</template>
		</Popover>
		<div class="vue-version-switch__switcher" v-if="displaySwitcher">
			<div class="vue-version-switch__option2">
				<input
					class="vue-version-switch__radio"
					type="radio"
					id="version-2"
					name="versionSelector"
					value="2"
					v-model="selectedVersion"
					@change="switchVersion"
				/>
				<label class="vue-version-switch__label" for="version-2">
					Modern
				</label>
			</div>
			<br />
			<div class="vue-version-switch__option1">
				<input
					class="vue-version-switch__radio"
					type="radio"
					id="version-1"
					name="versionSelector"
					value="1"
					v-model="selectedVersion"
					@change="switchVersion"
				/>
				<label class="vue-version-switch__label" for="version-1">
					Classic
				</label>
			</div>
			<button
				class="vue-version-switch__hide-switcher"
				v-if="displaySwitcher"
				@click="toggleSwitcher"
			>
				<i class="icon icon-remove" />
			</button>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import getCompanyId from '@/composables/get-companyid';
import Tooltip from '@/components/shells/tooltip/tooltip.vue';
import Popover from '@/components/shells/popover/popover.vue';
import cookies from 'vue-cookies';

export default {
	name: 'VersionSwitch',
	components: {
		Tooltip,
		Popover
	},
	data() {
		return {
			selectedVersion: this.version,
			displaySwitcher: false,
			popoverCloseClicked: false
		};
	},
	props: {
		version: {
			type: Number,
			required: true
		},
		// DEV NOTE: when the user first sees the popover and closes it, this variable will be true.
		alreadyDisplayed: {
			type: Boolean,
			default: false
		},
		setPageVersion: {
			type: Boolean,
			default: false
		},
		popoverOptions: {
			type: Object,
			default: () => {
				return {
					container: 'app',
					zIndex: 1000
				};
			}
		}
	},
	created() {
		this.selectedVersion = this.version;

		if (this.setPageVersion) {
			this.setPageVersionConfigKeyId(this.$route);
		}
	},
	methods: {
		...mapActions('user', [
			'switchVersionAction',
			'setPageVersionConfigKeyIdAction'
		]),
		async switchVersion() {
			this.$emit('startChange', this.selectedVersion);

			await this.switchVersionAction({
				version: this.selectedVersion,
				companyId: getCompanyId(this.$route),
				configurationKeyId: this.configurationKeyId,
				employeeId: this.employeeId
			});

			this.$emit('change', this.selectedVersion);
		},
		toggleSwitcher(e) {
			e.stopPropagation();
			this.displaySwitcher = !this.displaySwitcher;
		},
		onPopoverClose() {
			this.setPopoverShown();
			this.popoverCloseClicked = true;
		},
		setPopoverShown() {
			cookies.set(this.popoverShownCookieKey, true, '7d');
		},
		isPopoverShown() {
			const shownVal = cookies.get(this.popoverShownCookieKey);
			let shown = false;

			if (shownVal) {
				shown = shownVal === 'true';
			}

			return shown;
		},
		setPageVersionConfigKeyId(route) {
			let id = 0;

			if (
				route.meta &&
				route.meta.accessWebRoute &&
				route.meta.configKeyId
			) {
				id = route.meta.configKeyId;
			}

			this.setPageVersionConfigKeyIdAction(id);
		}
	},
	computed: {
		...mapGetters('user', ['employeeId', 'pageVersionConfigKeyId']),
		showTooltip() {
			return !this.popoverVisible;
		},
		popoverShownCookieKey() {
			return `${this.configurationKeyId}_shown`;
		},
		configurationKeyId() {
			return this.pageVersionConfigKeyId;
		},
		visible() {
			return this.configurationKeyId > 0;
		},
		popoverVisible() {
			let isVisible = this.popoverCloseClicked;

			if (this.pageVersionConfigKeyId > 0) {
				isVisible = !this.isPopoverShown();
			}

			return isVisible;
		}
	},
	watch: {
		// eslint-disable-next-line no-unused-vars
		$route(to, from) {
			this.setPageVersionConfigKeyId(to);
		}
	}
};
</script>

<style lang="scss" scoped>
.vue-version-switch {
	margin-left: $gutter-md;

	&__display-switcher-btn {
		display: flex;
		@include clear-btn-styles;
		font-size: 22px;
		cursor: pointer;
	}

	&__hide-switcher {
		@include clear-btn-styles;
		display: inline-flex;
		margin-left: $gutter-xs;
		padding: $padding-xs;

		&:focus,
		&:active {
			outline: $focus-width $focus-style var(--brand-2);
		}
	}

	&__switcher {
		display: flex;
		align-items: center;
		font-size: $txt-supporting-font-size;
	}

	&__option1,
	&__option2 {
		position: relative;
	}

	&__radio {
		appearance: none;
		-webkit-appearance: none;
		position: absolute;

		&:checked + .vue-version-switch__label,
		&:focus + .vue-version-switch__label,
		&:hover + .vue-version-switch__label {
			position: relative;
			background: $ui-positive-light;

			&::after {
				content: '';
				position: absolute;
				left: -1px;
				@include transform-vertical-center;
				width: calc(100% + 2px);
				height: calc(100% + 2px);
				border: 2px solid $brand-2;
				z-index: $z-level1-2;
			}
		}
	}

	&__label {
		padding: $padding-xs $padding-md;
		border: 1px solid $neutral-3;
		border-radius: 2px;
		cursor: pointer;

		&:first-child {
			border-top-right-radius: 0;
			border-bottom-right-radius: 0;
		}

		&:last-child {
			margin-left: -1px;
			border-top-left-radius: 0;
			border-bottom-left-radius: 0;
		}
	}

	&__popover-title {
		display: flex;

		.icon.icon-warning {
			font-size: 18px;
		}
	}
}
</style>
